import { ArrayUtils, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { BooleanConfig } from '../../config/BooleanConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord, CoordFailure } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { NInARowHelper } from '../../jscaip/NInARowHelper';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { TeekoDropMove, TeekoTranslationMove } from './TeekoMove';
import { TeekoState } from './TeekoState';
export class TeekoNode extends GameNode {
}
export class TeekoRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static get() {
        if (TeekoRules.singleton.isAbsent()) {
            TeekoRules.singleton = MGPOptional.of(new TeekoRules());
        }
        return TeekoRules.singleton.get();
    }
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Standard Teeko`,
        config: {
            teleport: new BooleanConfig(false, () => $localize `Piece can teleport`),
        },
    }, [{
            name: () => $localize `Teleport Teeko`,
            config: {
                teleport: true,
            },
        }]);
    static TEEKO_HELPER = new NInARowHelper(Utils.identity, 4);
    getRulesConfigDescription() {
        return TeekoRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState() {
        const board = TableUtils.create(TeekoState.WIDTH, TeekoState.WIDTH, PlayerOrNone.NONE);
        return new TeekoState(board, 0);
    }
    isLegal(move, state, config) {
        if (state.isInDropPhase()) {
            Utils.assert(move instanceof TeekoDropMove, 'Cannot translate in dropping phase !');
            return this.isLegalDrop(move, state);
        }
        else {
            Utils.assert(move instanceof TeekoTranslationMove, 'Cannot drop in translation phase !');
            return this.isLegalTranslation(move, state, config);
        }
    }
    isLegalDrop(move, state) {
        if (state.isNotOnBoard(move.coord)) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.coord));
        }
        if (state.getPieceAt(move.coord).isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        return MGPValidation.SUCCESS;
    }
    isLegalTranslation(move, state, config) {
        if (state.isNotOnBoard(move.getStart())) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.getStart()));
        }
        if (state.isNotOnBoard(move.getEnd())) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(move.getEnd()));
        }
        const translatedPiece = state.getPieceAt(move.getStart());
        if (translatedPiece === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        else if (translatedPiece.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (state.getPieceAt(move.getEnd()).isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        if (config.teleport === false) {
            if (move.getStart().isNeighborWith(move.getEnd()) === false) {
                return MGPValidation.failure(RulesFailure.MUST_MOVE_ON_NEIGHBOR());
            }
        }
        return MGPValidation.SUCCESS;
    }
    applyLegalMove(move, state, _config, _info) {
        if (move instanceof TeekoDropMove) {
            return this.applyLegalDrop(move, state);
        }
        else {
            return this.applyLegalTranslation(move, state);
        }
    }
    applyLegalDrop(move, state) {
        const newBoard = state.getCopiedBoard();
        newBoard[move.coord.y][move.coord.x] = state.getCurrentPlayer();
        return new TeekoState(newBoard, state.turn + 1);
    }
    applyLegalTranslation(move, state) {
        const newBoard = state.getCopiedBoard();
        newBoard[move.getStart().y][move.getStart().x] = PlayerOrNone.NONE;
        newBoard[move.getEnd().y][move.getEnd().x] = state.getCurrentPlayer();
        return new TeekoState(newBoard, state.turn + 1);
    }
    getGameStatus(node) {
        const state = node.gameState;
        const victoriousCoord = this.getVictoryCoord(state);
        if (victoriousCoord.length > 0) {
            return GameStatus.getVictory(state.getCurrentOpponent());
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    getLastCoord(move) {
        if (move instanceof TeekoDropMove) {
            return move.coord;
        }
        else {
            return move.getEnd();
        }
    }
    getSquareInfo(state) {
        const victoriousCoords = [];
        const possibilies = PlayerNumberMap.of(0, 0);
        for (let cx = 0; cx < TeekoState.WIDTH - 1; cx++) {
            for (let cy = 0; cy < TeekoState.WIDTH - 1; cy++) {
                const upLeft = new Coord(cx, cy);
                const upRight = new Coord(cx + 1, cy);
                const downLeft = new Coord(cx, cy + 1);
                const downRight = new Coord(cx + 1, cy + 1);
                const pieces = [
                    state.getPieceAt(upLeft),
                    state.getPieceAt(upRight),
                    state.getPieceAt(downLeft),
                    state.getPieceAt(downRight),
                ];
                const neutralCount = ArrayUtils.count(pieces, PlayerOrNone.NONE);
                const zeroCount = ArrayUtils.count(pieces, PlayerOrNone.ZERO);
                const oneCount = ArrayUtils.count(pieces, PlayerOrNone.ONE);
                if (neutralCount < 4) { // If the square has pieces
                    if (zeroCount === 4 || oneCount === 4) { // There is one player who won, in this square
                        victoriousCoords.push(upLeft, upRight, downLeft, downRight);
                    }
                    else if (zeroCount > 0) { // Only Player.ZERO has pieces in this square
                        possibilies.add(Player.ZERO, 1);
                    }
                    else { // Only Player.ONE has pieces in this square
                        possibilies.add(Player.ONE, 1);
                    }
                }
            }
        }
        return {
            score: possibilies.get(Player.ONE) - possibilies.get(Player.ZERO),
            victoriousCoords,
        };
    }
    getVictoryCoord(state) {
        // Concatenate line victories to square victories
        const linesVictories = TeekoRules.TEEKO_HELPER.getVictoriousCoord(state);
        const squareVictories = this.getSquareInfo(state).victoriousCoords;
        return linesVictories.concat(squareVictories);
    }
}
